import { type PriceTypes } from '../models/pricing.models';

const PERCENTAGE_DIVISOR = 100;

type MembershipType = {
  membershipTypeId: string;
  billingTemplateId?: string | null;
  discountMode: 'BASIC' | 'UNITS' | 'CATEGORIES';
  discount: number;
  discounts: {
    targetId: string;
    discount: number;
  }[];
};

type InvoiceTemplate = {
  invoiceTemplateId: string;
  items: {
    invoiceTemplateSkuId: string;
  }[];
};

type ExtendedInvoiceTemplate = InvoiceTemplate & {
  invoiceTemplateItemSkuId: string;
};

export type ExtendedMembershipType = MembershipType & {
  invoiceTemplates: ExtendedInvoiceTemplate[];
};

export type ProductData = {
  appointmentProposalOptionProductId: string;
  price: number;
  pricebookId: string;
  categoryIds: string[];
  defaultPriceType: PriceTypes;
  pricebookPrices: {
    priceType: PriceTypes;
    value: number;
    currency: 'USD';
    taxable: boolean;
  }[];
};

export type ProductDataFinancing = {
  appointmentProposalOptionProductId: string;
  hasMembership: boolean;
  membershipPrice: number;
  membershipPricebookIds: string[];
};

export interface AppointmentProposalOptionMembershipPricesRequest {
  filters: {
    businessUnitId?: string;
    activeMembershipTypes?: string[];
  };
  products: ProductData[];
  membershipTypes: MembershipType[];
  invoiceTemplates: InvoiceTemplate[];
}

function getMembershipsInOption(
  products: ProductData[],
  membershipTypes: MembershipType[],
  invoiceTemplates: InvoiceTemplate[],
  activeMembershipTypes: Set<string>
): ExtendedMembershipType[] {
  const invoiceTemplateItems: ExtendedInvoiceTemplate[] =
    invoiceTemplates.flatMap((it) =>
      it.items.map((iti) => ({
        invoiceTemplateId: it.invoiceTemplateId,
        invoiceTemplateItemSkuId: iti.invoiceTemplateSkuId,
        items: it.items,
      }))
    );

  const extendedMembershipTypes: ExtendedMembershipType[] = membershipTypes.map(
    (mt) => ({
      discount: mt.discount,
      membershipTypeId: mt.membershipTypeId,
      discountMode: mt.discountMode,
      discounts: mt.discounts,
      invoiceTemplates: invoiceTemplateItems.filter(
        (iti) => iti.invoiceTemplateId === mt.billingTemplateId
      ),
    })
  );

  const productsPricebookIds = new Set(products.map((p) => p.pricebookId));

  const matchingInvoiceTemplateItems = new Set(
    invoiceTemplateItems
      .filter((iti) => productsPricebookIds.has(iti.invoiceTemplateItemSkuId))
      .map((miti) => miti.invoiceTemplateItemSkuId)
  );

  return extendedMembershipTypes.filter(
    (mt) =>
      mt.invoiceTemplates.some((iti) =>
        matchingInvoiceTemplateItems.has(iti.invoiceTemplateItemSkuId)
      ) || activeMembershipTypes.has(mt.membershipTypeId)
  );
}

function getPrices(product: ProductData) {
  const isAddon = product.defaultPriceType === 'STANDARD_ADDON';

  const standardPrice =
    product.pricebookPrices.find((p) => p.priceType === 'STANDARD')?.value ?? 0;
  const standardAddonPrice =
    product.pricebookPrices.find((p) => p.priceType === 'STANDARD_ADDON')
      ?.value ?? undefined;

  const price =
    isAddon && standardAddonPrice ? standardAddonPrice : standardPrice;

  const memberPrice = isAddon
    ? (product.pricebookPrices.find((p) => p.priceType === 'MEMBER_ADDON')
        ?.value ?? undefined)
    : (product.pricebookPrices.find((p) => p.priceType === 'MEMBER')?.value ??
      undefined);

  const isCustomPrice =
    product.defaultPriceType === 'MANUAL' ||
    ![standardPrice, standardAddonPrice, memberPrice].some(
      (p) => p === product.price
    );
  return { price, memberPrice, isCustomPrice };
}

function normalizeProduct(product: ProductData): ProductDataFinancing {
  return {
    appointmentProposalOptionProductId:
      product.appointmentProposalOptionProductId,
    hasMembership: false,
    membershipPrice: 0,
    membershipPricebookIds: [],
  };
}

function getMembershipDiscount(
  product: ProductData,
  membershipType: ExtendedMembershipType,
  businessUnitId?: string
) {
  switch (membershipType.discountMode) {
    case 'BASIC':
      return membershipType.discount;
    case 'UNITS':
      return !businessUnitId
        ? undefined
        : (membershipType.discounts.find((t) => t.targetId === businessUnitId)
            ?.discount ?? undefined);
    case 'CATEGORIES': {
      const categoriesSet = new Set<string>(product.categoryIds);

      const applicableDiscounts = membershipType.discounts
        .filter((discount) => categoriesSet.has(discount.targetId))
        .map((discount) => discount.discount);

      return !applicableDiscounts.length
        ? undefined
        : Math.max(...applicableDiscounts);
    }
  }
}

function applyMembership(
  product: ProductData,
  membershipPricebookIds: Set<string>,
  membershipTypes: ExtendedMembershipType[],
  businessUnitId?: string
): ProductDataFinancing {
  const { price, memberPrice, isCustomPrice } = getPrices(product);

  if (
    membershipPricebookIds.has(product.pricebookId) ||
    price <= 0 ||
    isCustomPrice
  ) {
    return normalizeProduct(product);
  }

  const discounts = membershipTypes
    .map((mt) => getMembershipDiscount(product, mt, businessUnitId))
    .filter((d) => d !== undefined);

  if (!discounts.length) {
    return normalizeProduct(product);
  }

  const discount = Math.max(...discounts);

  const calculatedMembershipPrice = Math.min(
    ...[
      (price * (PERCENTAGE_DIVISOR - discount)) / PERCENTAGE_DIVISOR,
      ...(memberPrice ? [memberPrice] : []),
    ]
  );

  return {
    appointmentProposalOptionProductId:
      product.appointmentProposalOptionProductId,
    membershipPricebookIds: Array.from(membershipPricebookIds),
    hasMembership: calculatedMembershipPrice !== price,
    membershipPrice: calculatedMembershipPrice,
  };
}

export function buildMembershipPrices(
  req: AppointmentProposalOptionMembershipPricesRequest
) {
  const { products, membershipTypes, invoiceTemplates } = req;

  const appliedMembershipTypes = getMembershipsInOption(
    products,
    membershipTypes,
    invoiceTemplates,
    new Set(req.filters.activeMembershipTypes || [])
  );

  if (!appliedMembershipTypes.length) {
    return {
      appliedMembershipTypes: [],
      products: products.map(normalizeProduct),
    };
  }

  const membershipPricebookIds = new Set(
    appliedMembershipTypes.flatMap((mt) =>
      mt.invoiceTemplates.map((iti) => iti.invoiceTemplateItemSkuId)
    )
  );

  return {
    appliedMembershipTypes,
    products: products.map((p) =>
      applyMembership(
        p,
        membershipPricebookIds,
        appliedMembershipTypes,
        req.filters.businessUnitId
      )
    ),
  };
}

export function mapProductPrices<T extends ProductData>(
  product: T,
  pricebookItems: Map<string, { prices: T['pricebookPrices'] }>
) {
  const pricebookPrices = product.pricebookPrices.length
    ? product.pricebookPrices
    : pricebookItems.get(product.pricebookId)?.prices;

  if (!pricebookPrices) {
    return [];
  }

  return [
    {
      ...product,
      pricebookPrices,
    },
  ];
}
